import React from "react";
import { Box, Button, Typography, CircularProgress, Snackbar } from "@mui/material";
import { Add } from "@mui/icons-material";
import { useForm, useFieldArray, FormProvider } from "react-hook-form";
import { useQuery, useMutation } from "@apollo/client";
import { GET_ROOMS_BY_BUSINESS } from "../../graphql/queries/care";
import { UPSERT_ROOMS } from "../../graphql/mutations/care";
import RoomEditor from "../pages/roomEditor";
import useBusinessFromStore from "../hooks/useBusinessFromStore";
import { toEnumRoomType } from "../../util/roomType";

type Bed = {
  id?: number | string;
  bedLabel?: string | null;
  gender?: "Male" | "Female" | "Any" | string;
  isOccupied?: boolean;
  notes?: string | null;
  hasPrivateBathroom?: boolean;
  hasHospitalBed?: boolean;
  hasHoyerLift?: boolean;
  hasOxygen?: boolean;
  accommodatesBariatric?: boolean;
};

type Room = {
  id?: number | string;
  roomNumber: string;
  roomType?: string | null;
  notes?: string | null;
  businessId: number;
  beds: Bed[];
};

type FormShape = { rooms: Room[] };

type Props = {
  businessId: number;
  hasActiveLead?: boolean;
  minRoomsRequired?: number;
  onBlockEdit?: (reason: "min-rooms" | "no-open-beds") => void;
  onAfterMutation?: () => void;
  onDirtyChange?: (dirty: boolean) => void;
  bindApi?: (api: { save: () => Promise<void>; cancel: () => void; isDirty: () => boolean }) => void;
};

const normalizeId = (v: unknown): number | string | undefined => {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isNaN(n) ? String(v as any) : n;
};

export default function RoomListEditor({
  businessId,
  hasActiveLead = false,
  minRoomsRequired = 0,
  onBlockEdit,
  onAfterMutation,
  onDirtyChange,
  bindApi,
}: Props) {
  const formMethods = useForm<FormShape>({ defaultValues: { rooms: [] }, mode: "onChange" });
  const { control, reset, getValues, watch, formState } = formMethods;
  const { reload } = useBusinessFromStore();

  const {
    fields: roomFields,
    append: appendRoom,
    remove: removeRoomBase,
    replace: replaceRooms,
  } = useFieldArray({ control, name: "rooms", keyName: "keyId" });

  const loadedSnapshotRef = React.useRef<FormShape>({ rooms: [] });
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [snackbarMsg, setSnackbarMsg] = React.useState("Saved!");

  const { loading, refetch } = useQuery(GET_ROOMS_BY_BUSINESS, {
    variables: { businessId },
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      const rooms: Room[] = (res?.getRoomsByBusiness ?? []).map((room: any) => ({
        id: room.id,
        businessId,
        roomNumber: (room.roomNumber ?? "").trim(),
        roomType: toEnumRoomType(room.roomType) ?? null,
        notes: room.notes ?? "",
        beds: (room.beds ?? []).length
          ? room.beds.map((bed: any, idx: number) => ({
            id: bed.id,
            bedLabel: (bed.bedLabel ?? "").trim() || `Bed ${idx + 1}`,
            gender: bed.gender ?? "Any",
            isOccupied: !!bed.isOccupied,
            notes: bed.notes ?? null,
            hasPrivateBathroom: !!bed.hasPrivateBathroom,
            hasHospitalBed: !!bed.hasHospitalBed,
            hasHoyerLift: !!bed.hasHoyerLift,
            hasOxygen: !!bed.hasOxygen,
            accommodatesBariatric: !!bed.accommodatesBariatric,
          }))
          : [
            {
              bedLabel: "Bed 1",
              gender: "Any",
              isOccupied: false,
              notes: null,
              hasPrivateBathroom: false,
              hasHospitalBed: false,
              hasHoyerLift: false,
              hasOxygen: false,
              accommodatesBariatric: false,
            },
          ],
      }));

      loadedSnapshotRef.current = { rooms };
      replaceRooms(rooms);
      reset({ rooms }, { keepDirty: false });
      onDirtyChange?.(false);
    },
  });

  const [upsertRooms, { loading: saving }] = useMutation(UPSERT_ROOMS);

  const rooms = watch("rooms") ?? [];
  const hasNewEntities = rooms.some((r) => !r?.id || (r.beds ?? []).some((b) => !b?.id));

  React.useEffect(() => {
    const dirty = (formState.isDirty || hasNewEntities) && !saving;
    onDirtyChange?.(dirty);
  }, [formState.isDirty, hasNewEntities, saving, onDirtyChange]);

  const { availableBeds, totalBeds } = React.useMemo(() => {
    let total = 0;
    let available = 0;
    for (const r of rooms) {
      const beds = Array.isArray(r.beds) ? r.beds : [];
      for (const b of beds) {
        total += 1;
        if (!b.isOccupied) available += 1;
      }
    }
    return { availableBeds: available, totalBeds: total };
  }, [rooms]);

  const handleAddRoom = () => {
    appendRoom({
      roomNumber: "",
      roomType: "",
      notes: "",
      businessId,
      beds: [
        {
          bedLabel: "Bed 1",
          gender: "Any",
          isOccupied: false,
          notes: null,
          hasPrivateBathroom: false,
          hasHospitalBed: false,
          hasHoyerLift: false,
          hasOxygen: false,
          accommodatesBariatric: false,
        },
      ],
    } as Room);
  };

  const removeRoomGuarded = (index: number) => {
    const current = getValues("rooms") ?? [];
    const nextRooms = current.filter((_, i) => i !== index);

    if (nextRooms.length < minRoomsRequired) {
      onBlockEdit?.("min-rooms");
      setSnackbarMsg("You have pending (UNLOCKED) referrals — at least one room must remain.");
      setSnackbarOpen(true);
      return;
    }

    if (hasActiveLead) {
      const nextOpenBeds = nextRooms.reduce((sum, r) => {
        const beds = Array.isArray(r.beds) ? r.beds : [];
        return sum + beds.filter((b) => !b.isOccupied).length;
      }, 0);
      if (nextOpenBeds === 0) {
        onBlockEdit?.("no-open-beds");
        setSnackbarMsg("You have pending (UNLOCKED) referrals — you cannot close all beds.");
        setSnackbarOpen(true);
        return;
      }
    }

    removeRoomBase(index);
  };

  const validateBeforeSave = (proposedRooms: Room[]) => {
    if (proposedRooms.length < minRoomsRequired) {
      onBlockEdit?.("min-rooms");
      setSnackbarMsg("You have pending (UNLOCKED) referrals — at least one room must remain.");
      setSnackbarOpen(true);
      return false;
    }
    if (hasActiveLead) {
      const proposedOpenBeds = proposedRooms.reduce((sum, r) => {
        const beds = Array.isArray(r.beds) ? r.beds : [];
        return sum + beds.filter((b) => !b.isOccupied).length;
      }, 0);
      if (proposedOpenBeds === 0) {
        onBlockEdit?.("no-open-beds");
        setSnackbarMsg("You have pending (UNLOCKED) referrals — you cannot save with all beds occupied.");
        setSnackbarOpen(true);
        return false;
      }
    }
    return true;
  };

  const save = async () => {
    const currentRooms = getValues("rooms");

    const toSave: Room[] = currentRooms.map((room) => {
      const normRoomId = normalizeId(room.id);
      return {
        ...(normRoomId !== undefined ? { id: normRoomId } : {}),
        businessId,
        roomNumber: room.roomNumber ?? "",
        roomType: room.roomType ?? "",
        notes: room.notes ?? "",
        beds: (room.beds || []).map((bed, idx) => {
          const normBedId = normalizeId(bed.id);
          const payload: Bed = {
            ...(normBedId !== undefined ? { id: normBedId } : {}),
            bedLabel: (bed.bedLabel ?? "").trim() || `Bed ${idx + 1}`,
            gender: (bed.gender as any) ?? "Any",
            isOccupied: !!bed.isOccupied,
            notes: bed.notes ?? null,
            hasPrivateBathroom: !!bed.hasPrivateBathroom,
            hasHospitalBed: !!bed.hasHospitalBed,
            hasHoyerLift: !!bed.hasHoyerLift,
            hasOxygen: !!bed.hasOxygen,
            accommodatesBariatric: !!bed.accommodatesBariatric,
          };
          return payload;
        }),
      } as Room;
    });

    if (!validateBeforeSave(toSave)) return;

    await upsertRooms({ variables: { businessId, rooms: toSave } });

    const refreshed = await refetch();
    const roomsFromServer: Room[] = (refreshed?.data?.getRoomsByBusiness ?? []).map((room: any) => ({
      id: room.id,
      roomNumber: room.roomNumber ?? "",
      roomType: room.roomType ?? "",
      notes: room.notes ?? "",
      businessId,
      beds: (room.beds ?? []).map((bed: any, idx: number) => ({
        id: bed.id,
        bedLabel: (bed.bedLabel ?? "").trim() || `Bed ${idx + 1}`,
        gender: bed.gender ?? "Any",
        isOccupied: !!bed.isOccupied,
        notes: bed.notes ?? null,
        hasPrivateBathroom: !!bed.hasPrivateBathroom,
        hasHospitalBed: !!bed.hasHospitalBed,
        hasHoyerLift: !!bed.hasHoyerLift,
        hasOxygen: !!bed.hasOxygen,
        accommodatesBariatric: !!bed.accommodatesBariatric,
      })),
    }));

    loadedSnapshotRef.current = { rooms: roomsFromServer };
    replaceRooms(roomsFromServer);
    reset({ rooms: roomsFromServer }, { keepDirty: false, keepValues: false });
    onDirtyChange?.(false);
    reload();
    onAfterMutation?.();
    setSnackbarMsg("Saved!");
    setSnackbarOpen(true);
  };

  const cancel = () => {
    replaceRooms(loadedSnapshotRef.current.rooms);
    reset(loadedSnapshotRef.current, { keepDirty: false, keepValues: false });
    onDirtyChange?.(false);
    setSnackbarMsg("Changes reverted");
    setSnackbarOpen(true);
  };

  React.useEffect(() => {
    bindApi?.({
      save,
      cancel,
      isDirty: () => (formState.isDirty || hasNewEntities) && !saving,
    });
  }, [bindApi, formState.isDirty, hasNewEntities, saving]);

  if (loading) return <CircularProgress />;

  const isDirtyLocal = formState.isDirty || hasNewEntities;
  const canSave = isDirtyLocal && !saving && roomFields.length > 0;

  return (
    <FormProvider {...formMethods}>
      <Box sx={{ mt: 3 }}>
        <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
          <Typography variant="subtitle2">Available Beds: {availableBeds} / {totalBeds}</Typography>
        </Box>

        {roomFields.map((room, roomIdx) => (
          <RoomEditor key={(room as any).keyId} roomIdx={roomIdx} removeRoom={() => removeRoomGuarded(roomIdx)} />
        ))}

        <Box display="flex" justifyContent="flex-end" mt={2} gap={1}>
          <Button variant="outlined" startIcon={<Add />} onClick={handleAddRoom} sx={{ mr: 1 }}>
            Add Room
          </Button>
          {isDirtyLocal && (
            <Button variant="text" color="inherit" onClick={cancel}>
              Cancel
            </Button>
          )}
          <Button type="button" variant="contained" color="primary" disabled={!canSave} onClick={save}>
            {saving ? "Saving..." : "Save"}
          </Button>
        </Box>

        <Snackbar open={snackbarOpen} onClose={() => setSnackbarOpen(false)} autoHideDuration={2200} message={snackbarMsg} />
      </Box>
    </FormProvider>
  );
}
